import type { DetectionBox, FaceDetectionDiagnostics, Rect } from "@aimosaic/core";
import faceDetectionFullBinarypbUrl from "@mediapipe/face_detection/face_detection_full.binarypb?url";
import faceDetectionFullRangeSparseTfliteUrl from "@mediapipe/face_detection/face_detection_full_range_sparse.tflite?url";
import faceDetectionFullRangeTfliteUrl from "@mediapipe/face_detection/face_detection_full_range.tflite?url";
import faceDetectionShortBinarypbUrl from "@mediapipe/face_detection/face_detection_short.binarypb?url";
import faceDetectionShortRangeTfliteUrl from "@mediapipe/face_detection/face_detection_short_range.tflite?url";
import faceDetectionSimdDataUrl from "@mediapipe/face_detection/face_detection_solution_simd_wasm_bin.data?url";
import faceDetectionSimdJsUrl from "@mediapipe/face_detection/face_detection_solution_simd_wasm_bin.js?url";
import faceDetectionSimdWasmUrl from "@mediapipe/face_detection/face_detection_solution_simd_wasm_bin.wasm?url";
import faceDetectionWasmJsUrl from "@mediapipe/face_detection/face_detection_solution_wasm_bin.js?url";
import faceDetectionWasmUrl from "@mediapipe/face_detection/face_detection_solution_wasm_bin.wasm?url";

type FaceDetectorConstructor = new (options?: { fastMode?: boolean; maxDetectedFaces?: number }) => {
  detect(source: CanvasImageSource): Promise<Array<{ boundingBox: DOMRectReadOnly }>>;
};

interface MediaPipeFace {
  bbox: Rect;
  confidence?: number;
}

interface MediaPipeFaceDetector {
  detect(image: HTMLImageElement): Promise<MediaPipeFace[]>;
}

type MediaPipeFaceModel = "short" | "full";
type MediaPipeFaceDetectorFactory = (model: MediaPipeFaceModel) => Promise<MediaPipeFaceDetector>;
type HumanFaceDetectorFactory = () => Promise<MediaPipeFaceDetector>;

declare global {
  interface Window {
    FaceDetector?: FaceDetectorConstructor;
  }
}

let mediaPipeDetectorPromises: Partial<Record<MediaPipeFaceModel, Promise<MediaPipeFaceDetector>>> = {};
let mediaPipeFactoryForTests: MediaPipeFaceDetectorFactory | null = null;
let humanFaceDetectorPromise: Promise<MediaPipeFaceDetector> | null = null;
let humanFaceFactoryForTests: HumanFaceDetectorFactory | null = null;
const HUMAN_FACE_MIN_CONFIDENCE = 0.38;
const HUMAN_GROUP_FACE_MIN_CONFIDENCE = 0.32;

const mediapipeAssetUrls: Record<string, string> = {
  "face_detection_full.binarypb": faceDetectionFullBinarypbUrl,
  "face_detection_full_range.tflite": faceDetectionFullRangeTfliteUrl,
  "face_detection_full_range_sparse.tflite": faceDetectionFullRangeSparseTfliteUrl,
  "face_detection_short.binarypb": faceDetectionShortBinarypbUrl,
  "face_detection_short_range.tflite": faceDetectionShortRangeTfliteUrl,
  "face_detection_solution_simd_wasm_bin.data": faceDetectionSimdDataUrl,
  "face_detection_solution_simd_wasm_bin.js": faceDetectionSimdJsUrl,
  "face_detection_solution_simd_wasm_bin.wasm": faceDetectionSimdWasmUrl,
  "face_detection_solution_wasm_bin.js": faceDetectionWasmJsUrl,
  "face_detection_solution_wasm_bin.wasm": faceDetectionWasmUrl
};

export interface FaceDetectionResult {
  boxes: DetectionBox[];
  diagnostics: FaceDetectionDiagnostics;
}

export function setMediaPipeFaceDetectorFactoryForTests(factory: MediaPipeFaceDetectorFactory | null): void {
  mediaPipeFactoryForTests = factory;
  mediaPipeDetectorPromises = {};
}

export function setHumanFaceDetectorFactoryForTests(factory: HumanFaceDetectorFactory | null): void {
  humanFaceFactoryForTests = factory;
  humanFaceDetectorPromise = null;
}

export async function detectFaces(image: HTMLImageElement): Promise<FaceDetectionResult> {
  const startedAt = performance.now();
  const nativeResult = await detectWithNativeFaceDetector(image, startedAt);
  if (nativeResult?.boxes.length) return nativeResult;

  let lastErrorCode = nativeResult?.diagnostics.errorCode;
  let lastErrorMessage = nativeResult?.diagnostics.errorMessage;
  let mediaPipeRan = false;
  const mediaPipeDetectors: MediaPipeFaceDetector[] = [];
  for (const model of mediaPipeModelOrder(image)) {
    try {
      const detector = await loadMediaPipeDetector(model);
      mediaPipeDetectors.push(detector);
      const faces = await detectWithMediaPipeRetry(detector, image);
      mediaPipeRan = true;
      if (faces.length > 0) return makeResult("mediapipe", faces, startedAt);
    } catch (error) {
      lastErrorCode = "mediapipe-unavailable";
      lastErrorMessage = errorMessage(error);
    }
  }

  try {
    const humanFaceDetector = await loadHumanFaceDetector();
    const faces = filterHumanFaces(await humanFaceDetector.detect(image));
    if (faces.length > 0 && mediaPipeRan) {
      const stabilizedFaces = await detectWithStabilizedMediaPipeSource(mediaPipeDetectors, image);
      if (stabilizedFaces.length > 0) return makeResult("mediapipe", stabilizedFaces, startedAt);
    }
    if (faces.length > 0 || !mediaPipeRan) return makeResult("human", faces, startedAt);
    return makeResult("human", [], startedAt);
  } catch (error) {
    lastErrorCode = "human-unavailable";
    lastErrorMessage = errorMessage(error);
  }

  if (mediaPipeRan) return makeResult("mediapipe", [], startedAt);
  if (nativeResult) return nativeResult;
  return {
    boxes: [],
    diagnostics: {
      engine: "unavailable",
      faceCount: 0,
      errorCode: lastErrorCode ?? "mediapipe-unavailable",
      errorMessage: lastErrorMessage,
      elapsedMs: elapsedSince(startedAt)
    }
  };
}

async function detectWithNativeFaceDetector(
  image: HTMLImageElement,
  startedAt: number
): Promise<FaceDetectionResult | null> {
  const nativeWindow = typeof window === "undefined" ? undefined : window;
  if (!nativeWindow?.FaceDetector) return null;

  try {
    const detector = new nativeWindow.FaceDetector({ fastMode: true, maxDetectedFaces: 20 });
    const faces = await detector.detect(image);
    return makeResult(
      "native",
      faces.map((face) => ({
        bbox: {
          x: face.boundingBox.x,
          y: face.boundingBox.y,
          width: face.boundingBox.width,
          height: face.boundingBox.height
        },
        confidence: 0.85
      })),
      startedAt
    );
  } catch (error) {
    return {
      boxes: [],
      diagnostics: {
        engine: "unavailable",
        faceCount: 0,
        errorCode: "native-face-detector-failed",
        errorMessage: errorMessage(error),
        elapsedMs: elapsedSince(startedAt)
      }
    };
  }
}

function makeResult(
  engine: FaceDetectionDiagnostics["engine"],
  faces: MediaPipeFace[],
  startedAt: number
): FaceDetectionResult {
  return {
    boxes: faces.map((face, index) => ({
      id: `face-${engine}-${index}`,
      category: "face",
      bbox: face.bbox,
      confidence: face.confidence ?? 0.85,
      source: "local-face",
      action: "mask"
    })),
    diagnostics: {
      engine,
      faceCount: faces.length,
      elapsedMs: elapsedSince(startedAt)
    }
  };
}

function mediaPipeModelOrder(image: HTMLImageElement): MediaPipeFaceModel[] {
  const shortestSide = Math.min(sourceWidth(image), sourceHeight(image));
  return shortestSide > 500 ? ["full", "short"] : ["short", "full"];
}

async function loadMediaPipeDetector(model: MediaPipeFaceModel): Promise<MediaPipeFaceDetector> {
  if (mediaPipeFactoryForTests) return mediaPipeFactoryForTests(model);
  mediaPipeDetectorPromises[model] ??= createMediaPipeDetector(model);
  return mediaPipeDetectorPromises[model];
}

async function loadHumanFaceDetector(): Promise<MediaPipeFaceDetector> {
  if (humanFaceFactoryForTests) return humanFaceFactoryForTests();
  humanFaceDetectorPromise ??= createHumanFaceDetector();
  return humanFaceDetectorPromise;
}

async function detectWithMediaPipeRetry(
  detector: MediaPipeFaceDetector,
  image: HTMLImageElement
): Promise<MediaPipeFace[]> {
  const faces = await withConnectedImageForMediaPipe(image, (source) => detector.detect(source));
  if (faces.length > 0) return faces;
  return withConnectedImageForMediaPipe(image, (source) => detector.detect(source));
}

async function detectWithStabilizedMediaPipeSource(
  detectors: MediaPipeFaceDetector[],
  image: HTMLImageElement
): Promise<MediaPipeFace[]> {
  for (const detector of detectors) {
    const faces = await withStableImageForMediaPipe(image, (source) => detector.detect(source));
    if (faces.length > 0) return faces;
  }
  return [];
}

async function withStableImageForMediaPipe<T>(
  image: HTMLImageElement,
  run: (source: HTMLImageElement) => Promise<T>
): Promise<T> {
  const source = createFreshImageForMediaPipe(image);
  return withConnectedImageForMediaPipe(source, run);
}

async function withConnectedImageForMediaPipe<T>(
  image: HTMLImageElement,
  run: (source: HTMLImageElement) => Promise<T>
): Promise<T> {
  const ownerDocument = typeof document === "undefined" ? undefined : document;
  if (!ownerDocument?.body) {
    await decodeImage(image);
    return run(image);
  }
  if (image.isConnected) {
    await decodeImage(image);
    return run(image);
  }

  const previousCssText = image.style.cssText;
  image.style.cssText = `${previousCssText};position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;opacity:0;pointer-events:none;`;
  ownerDocument.body.appendChild(image);

  try {
    await decodeImage(image);
    return await run(image);
  } finally {
    if (image.parentNode === ownerDocument.body) {
      ownerDocument.body.removeChild(image);
    }
    image.style.cssText = previousCssText;
  }
}

async function decodeImage(image: HTMLImageElement): Promise<void> {
  if (typeof image.decode === "function") {
    await image.decode().catch(() => undefined);
  }
}

function createFreshImageForMediaPipe(image: HTMLImageElement): HTMLImageElement {
  const ownerDocument = typeof document === "undefined" ? undefined : document;
  if (!ownerDocument?.createElement) return image;
  const sourceUrl = image.currentSrc || image.src;
  if (!sourceUrl) return image;

  const next = ownerDocument.createElement("img");
  next.src = sourceUrl;
  next.decoding = "async";
  next.width = sourceWidth(image);
  next.height = sourceHeight(image);
  return next;
}

async function createMediaPipeDetector(model: MediaPipeFaceModel): Promise<MediaPipeFaceDetector> {
  const { FaceDetection } = await import("@mediapipe/face_detection");
  const detector = new FaceDetection({
    locateFile: (path) => mediapipeAssetUrls[path] ?? mediapipeAssetUrls[path.split("/").pop() ?? ""] ?? path
  });
  detector.setOptions({ model, minDetectionConfidence: model === "full" ? 0.45 : 0.5 });

  return {
    async detect(image) {
      const imageWidth = sourceWidth(image);
      const imageHeight = sourceHeight(image);
      const detections = await new Promise<MediaPipeFace[]>((resolve, reject) => {
        let settled = false;
        detector.onResults((results) => {
          if (settled) return;
          settled = true;
          resolve(
            (results.detections ?? []).flatMap((detection) =>
              normalizedRectToFace(detection.boundingBox, imageWidth, imageHeight)
            )
          );
        });
        detector.send({ image }).catch((error) => {
          if (settled) return;
          settled = true;
          reject(error instanceof Error ? error : new Error("mediapipe-face-detection-failed"));
        });
      });
      return detections;
    }
  };
}

async function createHumanFaceDetector(): Promise<MediaPipeFaceDetector> {
  const { Human } = await import("@vladmandic/human");
  const human = new Human({
    backend: "webgl",
    debug: false,
    modelBasePath: "/vendor/human/",
    cacheSensitivity: 0,
    filter: { enabled: false },
    face: {
      enabled: true,
      detector: {
        enabled: true,
        maxDetected: 20,
        minConfidence: HUMAN_GROUP_FACE_MIN_CONFIDENCE,
        minSize: 24,
        rotation: false,
        return: false,
        square: false
      },
      mesh: { enabled: false },
      iris: { enabled: false },
      description: { enabled: false },
      emotion: { enabled: false },
      antispoof: { enabled: false },
      liveness: { enabled: false }
    },
    body: { enabled: false },
    hand: { enabled: false },
    object: { enabled: false },
    gesture: { enabled: false },
    segmentation: { enabled: false }
  });
  await human.load();

  return {
    async detect(image) {
      const result = await human.detect(image);
      return result.face.flatMap((face) => {
        const [x, y, width, height] = face.box;
        if (!Number.isFinite(x) || !Number.isFinite(y) || width <= 0 || height <= 0) return [];
        return [
          {
            bbox: { x, y, width, height },
            confidence: Number.isFinite(face.boxScore) ? face.boxScore : 0.82
          }
        ];
      });
    }
  };
}

function filterHumanFaces(faces: MediaPipeFace[]): MediaPipeFace[] {
  const strongFaces = faces.filter((face) => face.confidence === undefined || face.confidence >= HUMAN_FACE_MIN_CONFIDENCE);
  if (strongFaces.length >= 5) {
    return faces.filter((face) => face.confidence === undefined || face.confidence >= HUMAN_GROUP_FACE_MIN_CONFIDENCE);
  }
  return strongFaces;
}

function normalizedRectToFace(rect: {
  xCenter: number;
  yCenter: number;
  width: number;
  height: number;
}, imageWidth: number, imageHeight: number): MediaPipeFace[] {
  const width = rect.width * imageWidth;
  const height = rect.height * imageHeight;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return [];
  return [
    {
      bbox: {
        x: rect.xCenter * imageWidth - width / 2,
        y: rect.yCenter * imageHeight - height / 2,
        width,
        height
      },
      confidence: 0.85
    }
  ];
}

function sourceWidth(image: HTMLImageElement): number {
  return Math.max(1, image.naturalWidth || image.width || 1);
}

function sourceHeight(image: HTMLImageElement): number {
  return Math.max(1, image.naturalHeight || image.height || 1);
}

function elapsedSince(startedAt: number): number {
  return Math.max(0, Math.round(performance.now() - startedAt));
}

function errorMessage(error: unknown): string | undefined {
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : undefined;
}
